import React from 'react';
import { useMutation } from '@apollo/client';
import { Button } from '@avila-tek/ui/src/Button';
import { useRouter } from 'next/router';
import { CREATE_ORDER } from '../../../graphql/mutation';
import { useNotify, useShopCart, useUser } from '../../../hooks';
import {
  Company,
  Order,
  PaymentMethod,
  PromoCode,
  TTally,
} from '../../../models';
import PaymentDataStep from './PaymentDataStep/PaymentDataStep';
import PaymentMethodStep from './PaymentMethodStep/PaymentMethodStep';
import PaymentAddressStep from './PaymentAddressStep/PaymentAddressStep';
import { ORDER_PAGINATION } from '../../../graphql/queries';

interface PaymentStepsProps {
  paymentMethods: PaymentMethod[];
  appliedPromoCodes: PromoCode[];
  total: TTally;
}

function PaymentSteps({
  paymentMethods,
  appliedPromoCodes,
  total,
}: PaymentStepsProps) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();
  const [shopCart, _, refreshShopCart] = useShopCart();
  const [disable, setDisable] = React.useState(false);

  const [stepSelected, setStepSelected] = React.useState<number>(1);
  const [paymentReference, setPaymentReference] = React.useState('');
  const [paymentMethodSelected, setPaymentMethodSelected] =
    React.useState<PaymentMethod>(null);

  const [createOrderMutation] = useMutation<{
    createOrder: Order;
  }>(CREATE_ORDER, {
    refetchQueries: [
      ORDER_PAGINATION, // DocumentNode object parsed with gql
      'orderPagination', // Query name
    ],
  });

  const createOrder = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      if (disable) return;
      setDisable(true);

      const { data } = await createOrderMutation({
        variables: {
          data: {
            shopCartId: shopCart?._id,
            promoCodes: appliedPromoCodes.map((promoCode) => promoCode?._id),
            billingAddress: (user?.company as Company)?.addresses[0]?._id,
            paymentInfo: {
              paymentMethod: paymentMethodSelected?._id,
              amount: total?.finalTotal,
              reference: null,
            },
          },
        },
      });
      if (data?.createOrder._id) {
        notify('Orden creada exitosamente', 'success');
        // necessary to update shopcart context with new car
        await refreshShopCart();
        router.push('/app/thanks');
      } else {
        notify('Error al crear la orden', 'error');
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
      setDisable(false);
    }
  };

  return (
    <div className="flex gap-5 flex-col">
      <PaymentMethodStep
        setStepSelected={setStepSelected}
        stepSelected={stepSelected}
        paymentMethods={paymentMethods}
        paymentMethodSelected={paymentMethodSelected}
        setPaymentMethodSelected={setPaymentMethodSelected}
      />
      <PaymentDataStep
        setStepSelected={setStepSelected}
        stepSelected={stepSelected}
        paymentMethodSelected={paymentMethodSelected}
        paymentReference={paymentReference}
        setPaymentReference={setPaymentReference}
      />
      <PaymentAddressStep
        setStepSelected={setStepSelected}
        stepSelected={stepSelected}
        address={(user.company as Company).addresses[0]}
      />
      {stepSelected === 3 ? <Button onClick={createOrder}>Pagar</Button> : null}
    </div>
  );
}

export default PaymentSteps;
